import '../../scss/main.scss';
import { listPublicEvents, getEvent } from '../shared/data/eventsRepo.js';
import { addVote, getVotesByRange } from '../shared/data/votesRepo.js';
import { signInAnonymouslySafe, onAuthChanged, getCurrentUser } from '../shared/firebase/auth.js';
import { renderVotesChart } from '../shared/charts/votesChartSvg.js';
import { el, show, hide, clear } from '../shared/ui/dom.js';
import { initOfflineBanner } from '../shared/ui/offlineBanner.js';
import { register as registerSW } from '../shared/pwa/registerSW.js';
import { getDeviceId, cacheCatalog } from '../shared/pwa/offlineCache.js';

// Estado de la aplicación
const state = {
    user: null,
    currentEventId: null,
    events: [],
    pendingVote: null, // Para guardar intención de voto si no está logueado
    eventPasswords: {} // Para guardar contraseñas de eventos privados (eventId: password)
};

// Elementos DOM principales
const mainContent = el('#main-content');
const loading = el('#loading');
const loginModal = el('#login-modal');
const passwordModal = el('#password-modal');
const offlineBanner = el('#offline-banner');
const userStatus = el('#user-status');
const hero = el('.hero');

// --- Inicialización ---

const init = async () => {
    // Inicializar banner offline con shared module
    initOfflineBanner();

    // Autenticar anónimamente si no hay usuario
    // Esto permite leer votos públicos sin que el usuario haga click
    try {
        const currentUser = getCurrentUser();
        if (!currentUser) {
            console.log('[Landing] Autenticando anónimamente para lectura de votos...');
            await signInAnonymouslySafe();
        }
    } catch (error) {
        console.warn('[Landing] No se pudo autenticar anónimamente:', error);
        // Continuar igualmente, algunos datos podrían no ser accesibles
    }

    // Listener de Auth usando nuevo module
    onAuthChanged((user) => {
        state.user = user;
        updateUserUI();
        // Si había un voto pendiente y acabamos de loguearnos, podríamos procesarlo
        // pero por simplicidad UX, dejamos que el usuario confirme de nuevo o cerramos modal.
        if (user && loginModal.open) {
            loginModal.close();
        }
    });

    // PWA Service Worker usando shared module
    registerSW().then(reg => {
        if (reg) console.log('[Landing] ✅ SW registrado');
    });

    // Inicializar Device ID usando shared module
    getDeviceId();

    // Router
    window.addEventListener('hashchange', router);
    
    // Carga inicial
    await router();
};

const updateOnlineStatus = () => {
    if (navigator.onLine) {
        hide(offlineBanner);
    } else {
        show(offlineBanner);
    }
};

const updateUserUI = () => {
    const btnLoginHeader = el('#btn-login-header');
    const btnCreateEventHeader = el('#btn-create-event-header');
    const heroCta = el('#hero-cta');

    if (state.user) {
        // Usuario autenticado
        if (state.user.isAnonymous) {
            // Usuario anónimo - mostrar botones de login y crear evento
            if (btnLoginHeader) btnLoginHeader.style.display = 'inline-block';
            if (btnCreateEventHeader) btnCreateEventHeader.style.display = 'inline-block';
            if (heroCta) heroCta.style.display = 'block';
        } else {
            // Usuario logueado con email - ocultar botones
            if (btnLoginHeader) btnLoginHeader.style.display = 'none';
            if (btnCreateEventHeader) btnCreateEventHeader.style.display = 'none';
            if (heroCta) heroCta.style.display = 'none';
        }
    } else {
        // Sin usuario
        if (btnLoginHeader) btnLoginHeader.style.display = 'none';
        if (btnCreateEventHeader) btnCreateEventHeader.style.display = 'none';
        if (heroCta) heroCta.style.display = 'none';
    }
};

// --- Router ---

const router = async () => {
    const hash = window.location.hash;
    
    // Resetear vista
    clear(mainContent);
    show(loading);
    mainContent.appendChild(loading);

    try {
        if (!hash || hash === '#/') {
            show(hero);
            await renderEventList();
        } else {
            hide(hero);
            if (hash.startsWith('#/event/')) {
                const id = hash.split('/')[2];
                if (id) {
                    await renderEventDetail(id);
                } else {
                    window.location.hash = '#/';
                }
            } else {
                window.location.hash = '#/';
            }
        }
    } catch (error) {
        console.error("[Landing] ❌ Router error:", error);
        mainContent.innerHTML = `<p class="error">Ha ocurrido un error cargando la página.</p>`;
    } finally {
        // Asegurarse de que el loading se quita si no se ha limpiado ya
        if (loading.parentNode) loading.remove();
    }
};

// --- Vistas ---

const renderEventList = async () => {
    // Usar template
    const tplContainer = el('#tpl-event-list').content.cloneNode(true);
    const listContainer = tplContainer.querySelector('#event-list-container');
    const tplCard = el('#tpl-event-card');

    try {
        const events = await listPublicEvents();
        state.events = events; // Cache simple
        // Guardar en cache usando shared module
        cacheCatalog(events);

        renderEventsToDom(events, listContainer, tplCard);
        
        mainContent.appendChild(listContainer);
    } catch (error) {
        console.warn("[Landing] ⚠️ Error cargando eventos, intentando cache local...", error);
        // Intentar recuperar de cache
        const { readCatalog } = await import('../shared/pwa/offlineCache.js');
        const cachedEvents = readCatalog();
        
        if (cachedEvents) {
            state.events = cachedEvents;
            renderEventsToDom(cachedEvents, listContainer, tplCard);
            const offlineMsg = document.createElement('p');
            offlineMsg.textContent = "Mostrando versión guardada (Offline)";
            offlineMsg.className = "offline-msg";
            listContainer.prepend(offlineMsg);
            mainContent.appendChild(listContainer);
        } else {
            mainContent.innerHTML = `<p>Error cargando eventos. Intenta de nuevo más tarde.</p>`;
        }
    }
};

const renderEventsToDom = (events, container, tplCard) => {
    if (events.length === 0) {
        container.innerHTML = '<p>No hay eventos públicos activos en este momento.</p>';
    } else {
        events.forEach(event => {
            const card = tplCard.content.cloneNode(true);
            
            // Rellenar datos
            const img = card.querySelector('.event-img');
            if (event.imagenUrl) img.src = event.imagenUrl;
            img.alt = event.titulo;
            
            card.querySelector('.event-title').textContent = event.titulo;
            // Para la descripción en cards, eliminamos etiquetas HTML para que se vea limpio
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = event.descripcion || '';
            card.querySelector('.event-desc').textContent = tempDiv.textContent || tempDiv.innerText || '';
            
            const btn = card.querySelector('.btn-view-more');
            btn.addEventListener('click', () => {
                window.location.hash = `#/event/${event.id}`;
            });

            container.appendChild(card);
        });
    }
};

const renderEventDetail = async (id) => {
    state.currentEventId = id;
    
    // Obtener datos
    let event = null;
    try {
        event = await getEvent(id);
    } catch (e) {
        console.warn("Offline or error fetching detail", e);
    }

    // Fallback a cache local si falla la red
    if (!event && state.events.length > 0) {
        event = state.events.find(e => e.id === id);
    } else if (!event) {
         // Intentar leer de LS si state.events está vacío (refresh en detalle offline)
         const cached = localStorage.getItem('cachedEvents');
         if (cached) {
             const events = JSON.parse(cached);
             state.events = events;
             event = events.find(e => e.id === id);
         }
    }
    
    if (!event) {
        mainContent.innerHTML = '<p>Evento no encontrado.</p><button class="btn btn-primary" onclick="window.location.hash=\'#/\'">Volver</button>';
        return;
    }

    // Validar contraseña si es evento privado
    if (!event.isPublico) {
        // Verificar si ya tenemos la contraseña guardada en sessionStorage
        const storedPassword = sessionStorage.getItem(`event_password_${id}`);
        
        if (!storedPassword) {
            // Mostrar modal de contraseña
            showPasswordModal(id);
            return;
        }
        // Si tenemos la contraseña guardada, continuamos con el renderizado
    }

    renderEventDetailContent(event, id);
};

/**
 * Renderiza el contenido del evento después de validar contraseña
 */
const renderEventDetailContent = (event, id) => {
    const tplDetail = el('#tpl-event-detail').content.cloneNode(true);
    
    // Limpiar loading
    clear(mainContent);

    // Rellenar UI
    tplDetail.querySelector('.btn-back').addEventListener('click', () => window.history.back());
    
    const img = tplDetail.querySelector('.detail-img');
    if (event.imagenUrl) img.src = event.imagenUrl;
    img.alt = event.titulo;
    
    tplDetail.querySelector('.detail-title').textContent = event.titulo;
    tplDetail.querySelector('.detail-desc').innerHTML = event.descripcion || '';

    // UI para evento cerrado
    const votingSection = tplDetail.querySelector('.voting-section');
    if (event.isCerrado) {
        const feedback = tplDetail.querySelector('#vote-feedback');
        if (feedback) {
            feedback.textContent = "Este evento ha finalizado. No se aceptan más votos.";
            feedback.style.color = "var(--primary)";
            feedback.style.fontWeight = "bold";
        }
        // Ocultar o deshabilitar botones de voto
        const voteButtons = tplDetail.querySelector('.vote-buttons');
        if (voteButtons) {
            voteButtons.style.opacity = '0.5';
            voteButtons.style.pointerEvents = 'none';
        }
    }

    // Listeners de Voto
    const btnVotes = tplDetail.querySelectorAll('.btn-vote');
    btnVotes.forEach(btn => {
        btn.addEventListener('click', (e) => handleVoteClick(e, id));
    });

    // Cargar Gráfico
    const chartContainer = tplDetail.querySelector('#chart-container');
    loadChart(id, chartContainer);

    mainContent.appendChild(tplDetail);
};

// --- Lógica de Votación ---

const handleVoteClick = async (e, eventId) => {
    const value = e.currentTarget.dataset.value;
    
    // Verificar si el evento está cerrado antes de proceder
    try {
        const event = await getEvent(eventId);
        if (event && event.isCerrado) {
            alert('Este evento está cerrado y ya no acepta más votos.');
            return;
        }
    } catch (error) {
        console.warn('Error al verificar estado del evento:', error);
    }

    if (!state.user) {
        state.pendingVote = { eventId, value };
        showLoginModal();
    } else {
        await submitVote(eventId, value);
    }
};

const submitVote = async (eventId, value) => {
    const feedback = el('#vote-feedback');
    if (feedback) feedback.textContent = "Enviando voto...";
    
    try {
        await addVote(eventId, value, {
            uid: state.user.uid,
            deviceId: localStorage.getItem('deviceId') || 'unknown'
        });
        
        if (feedback) {
            feedback.textContent = "¡Gracias por tu voto!";
            feedback.style.color = "green";
        }
        
        // Recargar gráfico
        const chartContainer = el('#chart-container');
        if (chartContainer) loadChart(eventId, chartContainer);

    } catch (error) {
        console.error(error);
        if (feedback) {
            feedback.textContent = "Error al votar.";
            feedback.style.color = "red";
        }
    }
};

const loadChart = async (eventId, container) => {
    container.innerHTML = 'Cargando gráfico...';
    try {
        // Obtenemos todos los votos para simplificar (idealmente filtrado por fecha)
        const votes = await getVotesByRange(eventId);
        console.log('[Landing] Votos obtenidos:', votes);
        renderVotesChart(votes, container);
        console.log('[Landing] Gráfico renderizado exitosamente');
    } catch (error) {
        console.error('[Landing] Error cargando gráfico:', error);
        container.innerHTML = 'Error cargando datos del gráfico.';
    }
};

// --- Modal Login ---

const showLoginModal = () => {
    if (typeof loginModal.showModal === "function") {
        loginModal.showModal();
    } else {
        // Fallback
        show(loginModal);
    }
};

el('#btn-close-modal').addEventListener('click', () => {
    loginModal.close();
    state.pendingVote = null;
});

el('#btn-login-anon').addEventListener('click', async () => {
    const btn = el('#btn-login-anon');
    btn.textContent = "Entrando...";
    btn.disabled = true;
    
    try {
        await signInAnonymouslySafe();
        // El listener de auth cerrará el modal
    } catch (error) {
        console.error('[Landing] ❌ Error login anónimo:', error);
        
        let msg = "Error al iniciar sesión anónima.";
        if (JSON.stringify(error).includes('CONFIGURATION_NOT_FOUND')) {
             msg += "\n\n⚠️ REQUERIDO: Habilitar 'Anónimo' en Firebase Console -> Authentication.";
        }
        
        alert(msg);
        btn.disabled = false;
        btn.textContent = "Continuar como Anónimo";
    }
});

// --- Modal de Contraseña para Eventos Privados ---

const showPasswordModal = (eventId) => {
    state.currentEventId = eventId;
    const passwordInput = el('#password-input');
    
    // Limpiar input y errores previos
    if (passwordInput) {
        passwordInput.value = '';
        passwordInput.style.borderColor = '';
    }
    
    if (typeof passwordModal.showModal === "function") {
        passwordModal.showModal();
    } else {
        show(passwordModal);
    }
};

el('#btn-verify-password').addEventListener('click', async () => {
    const passwordInput = el('#password-input');
    const password = passwordInput?.value || '';
    const eventId = state.currentEventId;
    
    if (!password) {
        if (passwordInput) {
            passwordInput.style.borderColor = '#f44336';
            passwordInput.focus();
        }
        return;
    }
    
    try {
        // Obtener el evento para verificar contraseña
        let event = state.events.find(e => e.id === eventId);
        if (!event) {
            event = await getEvent(eventId);
        }
        
        if (!event) {
            alert('Evento no encontrado');
            return;
        }
        
        // Verificar contraseña
        if (event.password && event.password === password) {
            // Guardar contraseña en sessionStorage
            sessionStorage.setItem(`event_password_${eventId}`, password);
            
            // Cerrar modal
            passwordModal.close();
            
            // Renderizar contenido del evento
            renderEventDetailContent(event, eventId);
        } else {
            // Contraseña incorrecta
            if (passwordInput) {
                passwordInput.style.borderColor = '#f44336';
                passwordInput.value = '';
            }
            alert('Contraseña incorrecta');
        }
    } catch (error) {
        console.error('Error verificando contraseña:', error);
        alert('Error al verificar contraseña');
    }
});

el('#btn-cancel-password').addEventListener('click', () => {
    passwordModal.close();
    window.location.hash = '#/';
});

// --- Botones de Header y CTA ---

/**
 * Navega al panel.html con autenticación
 */
const navigateToPanel = () => {
    // Redirigir a panel.html - el panel manejará la autenticación
    window.location.href = '/panel.html';
};

el('#btn-login-header')?.addEventListener('click', navigateToPanel);
el('#btn-create-event-header')?.addEventListener('click', navigateToPanel);
el('#btn-cta-create-event')?.addEventListener('click', navigateToPanel);

// Arrancar app
document.addEventListener('DOMContentLoaded', init);
